import { useState } from 'react';
import type { AssetAllocationResponse, AssetAllocationSlice } from '../api/chartDataTypes';
import { buildAssetAllocationSummarySentence } from '../utils/assetAllocationFormat';
import { formatReportDate, reportTypeLabel } from '../utils/perf10kFormat';
import { AssetAllocationSelector } from './AssetAllocationSelector';
import { ChartDataSectionShell } from './ChartDataSectionShell';

type AssetAllocationChartPanelProps = {
    response: AssetAllocationResponse;
};

const PROMPT_SUMMARY = 'Select Asset type to hear its Allocation';
const EMPTY_SUMMARY = 'No asset allocation data available.';

/**
 * Asset Allocation orchestrator. Mirrors EconSectorChartPanel — a
 * point-in-time snapshot keyed by asset-type label rather than date. The
 * shell's banner opens with a prompt and the user tabs into the Data Series
 * dropdown to hear each slice's weighting.
 */
export function AssetAllocationChartPanel({ response }: AssetAllocationChartPanelProps) {
    const { security, title, report_date, data } = response;
    const { slices } = data;

    const [selectedSlice, setSelectedSlice] = useState<AssetAllocationSlice | null>(null);

    const headerTitle = `${security.name} (${security.ticker}) — ${title}`;
    const headerSubtitle = `${reportTypeLabel(security.report_type)} · Report date: ${formatReportDate(report_date)}`;

    const hasSlices = slices.length > 0;

    let summary: string;
    let summaryHTML: string | undefined;
    if (!hasSlices) {
        summary = EMPTY_SUMMARY;
    } else if (selectedSlice === null) {
        summary = PROMPT_SUMMARY;
    } else {
        const parts = buildAssetAllocationSummarySentence(selectedSlice);
        summary = parts.text;
        summaryHTML = parts.html;
    }

    return (
        <ChartDataSectionShell
            title={headerTitle}
            subtitle={headerSubtitle}
            summary={summary}
            summaryHTML={summaryHTML}>
            {hasSlices ? (
                <AssetAllocationSelector
                    slices={slices}
                    idPrefix='asset_allocation'
                    onSelectedSliceChange={setSelectedSlice}
                />
            ) : null}
        </ChartDataSectionShell>
    );
}
